import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { MapContainer, ClusterMarkers } from 'features/Map'
import type { ClusterPoint } from 'features/Map'
import { tradeAreaService } from 'features/Map/services/tradeAreaService'

type LocationCategory = 'archived' | 'center' | 'poi' | 'tenant' | 'active'

const PIN_COLORS: Partial<Record<LocationCategory, string>> = {
	poi: '#F97316',
	tenant: '#22C55E',
}

const ARCHIVED_STYLE = { border: '#9CA3AF', overlay: 'rgba(107,114,128,0.45)' }
const AVATAR_BORDER_COLOR = '#0080FE'

export interface LocationMarkerData {
	id: string
	lat: number
	lng: number
	name?: string
	address?: string
	city?: string
	state?: string
	postalCode?: string
	image?: string
	category: LocationCategory
}

interface LocationsListMapProps {
	markers: LocationMarkerData[]
	center: number[]
	zoom: number
}

const MARKER_SIZE = 40

function escapeHtml(v: string): string {
	return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function buildPopupHTML(m: LocationMarkerData): string {
	const imgBlock = m.image
		? `<img src="${escapeHtml(m.image)}" alt="${escapeHtml(m.name || '')}" style="width:64px;height:64px;object-fit:cover;border-radius:6px;flex-shrink:0;" />`
		: `<div style="width:64px;height:64px;border-radius:6px;background:#f3f4f6;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:22px;font-weight:600;flex-shrink:0;">${escapeHtml((m.name || '?')[0])}</div>`
	const addressParts = [m.city, m.state, m.postalCode].filter(Boolean).join(', ')
	return `
		<div style="padding:4px;min-width:200px;">
			<div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
				${imgBlock}
				<span style="font-weight:600;font-size:14px;color:#111827;">${escapeHtml(m.name || '')}</span>
			</div>
			<div style="text-align:center;font-size:12px;color:#6b7280;line-height:1.6;">
				${m.address ? `<div>${escapeHtml(m.address)}</div>` : ''}
				${addressParts ? `<div>${escapeHtml(addressParts)}</div>` : ''}
			</div>
		</div>
	`
}

function buildTeardropPin(color: string): HTMLDivElement {
	const el = document.createElement('div')
	el.style.cssText = 'width:24px;height:32px;cursor:pointer;transition:transform 150ms ease;'
	el.innerHTML = `
		<svg viewBox="0 0 24 32" width="24" height="32" xmlns="http://www.w3.org/2000/svg">
			<path d="M12 0C5.37 0 0 5.37 0 12c0 9 12 20 12 20s12-11 12-20C24 5.37 18.63 0 12 0z"
				fill="${color}" stroke="#ffffff" stroke-width="2"/>
			<circle cx="12" cy="12" r="4" fill="#ffffff"/>
		</svg>
	`
	el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.15)' })
	el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)' })
	return el
}

function buildAvatarMarker(image: string, borderColor: string, isArchived: boolean, name?: string): HTMLButtonElement {
	const btn = document.createElement('button')
	btn.type = 'button'
	if (name) btn.title = name
	btn.style.cssText = `
		position:relative;display:block;padding:0;margin:0;
		width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;
		border-radius:50%;overflow:hidden;
		border:2px solid ${borderColor};
		box-shadow:0 2px 6px rgba(0,0,0,0.25);
		cursor:pointer;transition:transform 150ms ease;
	`
	btn.innerHTML = `
		<img src="${escapeHtml(image)}" alt="${escapeHtml(name || '')}" referrerpolicy="no-referrer"
			style="width:100%;height:100%;object-fit:cover;${isArchived ? 'filter:grayscale(1);' : ''}" />
		${isArchived ? '<span style="position:absolute;inset:0;border-radius:50%;background:rgba(107,114,128,0.45);pointer-events:none;"></span>' : ''}
	`
	btn.addEventListener('mouseenter', () => { btn.style.transform = 'scale(1.1)' })
	btn.addEventListener('mouseleave', () => { btn.style.transform = 'scale(1)' })
	return btn
}

function buildFallbackPin(color: string): HTMLDivElement {
	const el = document.createElement('div')
	el.style.cssText = `
		width:18px;height:18px;border-radius:50%;
		background:${color};border:2px solid #ffffff;
		box-shadow:0 2px 4px rgba(0,0,0,0.3);
		cursor:pointer;transition:transform 150ms ease;
	`
	el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.2)' })
	el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)' })
	return el
}

function renderLocationLeaf(point: ClusterPoint, onClick: () => void): HTMLElement {
	const props = point.properties as Partial<LocationMarkerData> | undefined
	const category = (props?.category ?? 'active') as LocationCategory
	const image = props?.image
	const name = props?.name

	let el: HTMLElement
	const pinColor = PIN_COLORS[category]
	if (pinColor) {
		el = buildTeardropPin(pinColor)
	} else if (image) {
		const isArchived = category === 'archived'
		const border = isArchived ? ARCHIVED_STYLE.border : AVATAR_BORDER_COLOR
		el = buildAvatarMarker(image, border, isArchived, name)
	} else {
		el = buildFallbackPin(category === 'archived' ? '#9CA3AF' : AVATAR_BORDER_COLOR)
	}
	el.addEventListener('click', (e) => {
		e.stopPropagation()
		onClick()
	})
	return el
}

const LocationsListMap = ({ markers, center, zoom }: LocationsListMapProps) => {
	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const popupRef = useRef<maplibregl.Popup | null>(null)
	const didInitRef = useRef(false)

	const points = useMemo<ClusterPoint[]>(
		() =>
			markers
				.filter((m) => m.lat != null && m.lng != null)
				.map((m) => ({
					id: m.id,
					longitude: m.lng,
					latitude: m.lat,
					properties: {
						category: m.category,
						name: m.name,
						address: m.address,
						city: m.city,
						state: m.state,
						postalCode: m.postalCode,
						image: m.image,
					},
				})),
		[markers],
	)

	const handleMapLoad = useCallback((m: maplibregl.Map) => {
		setMap(m)
	}, [])

	useEffect(() => {
		if (!map || didInitRef.current) return
		if (markers.length > 1) {
			const bounds = tradeAreaService.getBoundsForSites(
				markers.map((m) => ({ longitude: m.lng, latitude: m.lat })),
			)
			map.fitBounds(bounds, { padding: 60, maxZoom: 15, animate: false })
		} else if (markers.length === 1) {
			map.setCenter([markers[0].lng, markers[0].lat])
			map.setZoom(zoom)
		} else {
			map.setCenter([center[1], center[0]])
			map.setZoom(zoom)
		}
		didInitRef.current = true
	}, [map, markers, center, zoom])

	const handleLeafClick = useCallback(
		(pointId: string) => {
			if (!map) return
			const marker = markers.find((m) => m.id === pointId)
			if (!marker) return
			popupRef.current?.remove()
			const popup = new maplibregl.Popup({ offset: 24, closeButton: true })
				.setLngLat([marker.lng, marker.lat])
				.setHTML(buildPopupHTML(marker))
				.addTo(map)
			popupRef.current = popup
		},
		[map, markers],
	)

	const renderLeaf = useCallback(renderLocationLeaf, [])

	useEffect(() => {
		if (!map) return
		const dismiss = () => popupRef.current?.remove()
		map.on('click', dismiss)
		return () => { map.off('click', dismiss) }
	}, [map])

	return (
		<div className="shadow-xl relative rounded-lg overflow-hidden h-[680px]">
			<MapContainer onMapLoad={handleMapLoad}>
				<ClusterMarkers
					map={map}
					isMapLoaded={Boolean(map)}
					points={points}
					renderLeaf={renderLeaf}
					onLeafClick={handleLeafClick}
				/>
			</MapContainer>
			<div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm rounded-lg px-5 py-2 shadow-md flex items-center gap-6 text-xs z-10">
				<div className="flex items-center gap-1.5">
					<span className="w-3 h-3 rounded-full shrink-0 bg-[#0080FE]" />
					<span className="text-gray-600">Retail Location</span>
				</div>
				<div className="flex items-center gap-1.5">
					<span className="w-3 h-3 rounded-full bg-red-500 border border-red-600 shrink-0" />
					<span className="text-gray-600">Retail Center</span>
				</div>
				<div className="flex items-center gap-1.5">
					<span className="w-3 h-3 rounded-full bg-orange-400 border border-orange-500 shrink-0" />
					<span className="text-gray-600">POI Only</span>
				</div>
				<div className="flex items-center gap-1.5">
					<span className="w-3 h-3 rounded-full bg-green-500 border border-green-600 shrink-0" />
					<span className="text-gray-600">Tenant Only</span>
				</div>
				<div className="flex items-center gap-1.5">
					<span className="w-3 h-3 rounded-full bg-gray-400 border border-gray-500 shrink-0" />
					<span className="text-gray-600">Archived</span>
				</div>
			</div>
		</div>
	)
}

export default LocationsListMap
